import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Trash2, Eye, Edit } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageAccessSection = ({ patient }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (patient) {
      fetchShares();
    }
  }, [patient]);

  const fetchShares = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/patients/${patient.id}/shares`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShares(response.data);
    } catch (error) {
      console.error('Error fetching shares:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAccess = async (shareId, userName) => {
    if (!window.confirm(`Are you sure you want to revoke access for ${userName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/shared-patients/${shareId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Access revoked successfully');
      fetchShares();
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  if (loading) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users size={20} style={{ color: '#A2DAD3' }} />
          Manage Access
        </h3>
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Users size={20} style={{ color: '#A2DAD3' }} />
        Manage Access
      </h3>

      {shares.length === 0 ? (
        <p className="text-sm text-gray-500">No one has access to this patient's data yet.</p>
      ) : (
        <div className="space-y-3">
          {shares.map((item) => (
            <motion.div
              key={item.share.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex-1">
                <p className="text-sm font-medium">{item.user.name}</p>
                <p className="text-xs text-gray-500">{item.user.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  {item.share.permissions === 'view' ? (
                    <span className="text-xs flex items-center gap-1" style={{ color: '#A2DAD3' }}>
                      <Eye size={12} />
                      View Only
                    </span>
                  ) : (
                    <span className="text-xs flex items-center gap-1" style={{ color: '#F9A7A7' }}>
                      <Edit size={12} />
                      Can Edit
                    </span>
                  )}
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">
                    {item.share.data_to_share.length} data types
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleRevokeAccess(item.share.id, item.user.name)}
                className="text-red-500 hover:text-red-700 p-2"
                title="Revoke Access"
              >
                <Trash2 size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageAccessSection;
